import { useState } from 'react';

export default function QuickStart({ navigate }) {
  const [copied, setCopied] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [os, setOs] = useState(() => {
    const ua = navigator.userAgent;
    if (ua.includes('Win')) return 'windows';
    if (ua.includes('Mac')) return 'mac';
    return 'mac'; // default to mac
  });

  function copy(text, label) {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(''), 2000);
  }

  const key = apiKey.trim() || 'YOUR_API_KEY';
  const hasKey = apiKey.trim().startsWith('am_live_');

  const windowsCmd = `powershell -ExecutionPolicy Bypass -Command "Invoke-WebRequest -Uri 'https://invoke-api.com/setup.ps1' -OutFile setup.ps1; .\\setup.ps1 -ApiKey '${key}'"`;
  const macCmd = `curl -sO https://invoke-api.com/setup.sh && bash setup.sh ${key}`;

  const setupCmd = os === 'windows' ? windowsCmd : macCmd;

  const s = {
    wrap: { maxWidth: 680, margin: '0 auto' },
    h1: { fontSize: 32, fontWeight: 700, marginBottom: 8, letterSpacing: '-0.02em' },
    sub: { fontSize: 16, color: 'var(--muted)', marginBottom: 40, lineHeight: 1.5 },
    step: { marginBottom: 40 },
    stepHeader: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 },
    stepNum: {
      width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(135deg, var(--accent), #6558d4)',
      color: '#fff', fontSize: 14, fontWeight: 700,
    },
    stepTitle: { fontSize: 18, fontWeight: 600 },
    stepDesc: { fontSize: 14, color: 'var(--muted)', marginBottom: 12, lineHeight: 1.6 },
    codeWrap: { position: 'relative', marginBottom: 8 },
    code: {
      background: 'var(--bg)', border: '1px solid var(--border)',
      borderRadius: 'var(--radius-sm)', padding: '16px 18px',
      fontFamily: 'monospace', fontSize: 13, lineHeight: 1.7,
      color: 'var(--accent2)', whiteSpace: 'pre-wrap', wordBreak: 'break-all',
    },
    copyBtn: {
      position: 'absolute', top: 10, right: 10,
      padding: '6px 16px', fontSize: 12, fontWeight: 600,
      border: '1px solid var(--accent)', borderRadius: 6,
      background: 'rgba(124,111,239,0.15)', color: 'var(--accent2)', cursor: 'pointer',
    },
    divider: { height: 1, background: 'var(--border)', margin: '40px 0' },
    primaryBtn: {
      padding: '12px 24px', borderRadius: 'var(--radius-sm)', fontSize: 15, fontWeight: 600,
      border: 'none', background: 'linear-gradient(135deg, var(--accent), #6558d4)',
      color: '#fff', cursor: 'pointer', boxShadow: '0 2px 8px rgba(124,111,239,0.3)',
    },
    highlight: {
      display: 'inline', padding: '1px 6px', borderRadius: 4,
      background: 'rgba(124,111,239,0.15)', color: 'var(--accent2)',
      fontFamily: 'monospace', fontSize: 12,
    },
    input: {
      width: '100%', padding: '12px 16px', borderRadius: 'var(--radius-sm)',
      border: '1px solid var(--border)', background: 'var(--bg2)',
      color: 'var(--text)', fontSize: 14, fontFamily: 'monospace',
      outline: 'none', boxSizing: 'border-box',
    },
    osTabs: { display: 'flex', gap: 6, marginBottom: 16 },
    osTab: (active) => ({
      padding: '8px 20px', borderRadius: 'var(--radius-sm)', fontSize: 13, fontWeight: 500,
      border: `1px solid ${active ? 'var(--accent)' : 'var(--border)'}`,
      background: active ? 'rgba(124,111,239,0.12)' : 'transparent',
      color: active ? 'var(--accent2)' : 'var(--muted)', cursor: 'pointer',
    }),
    successBox: {
      background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.2)',
      borderRadius: 'var(--radius-sm)', padding: '16px 20px', marginBottom: 8,
    },
    reqBox: {
      background: 'rgba(234,179,8,0.08)', border: '1px solid rgba(234,179,8,0.25)',
      borderRadius: 'var(--radius-sm)', padding: '14px 18px', marginBottom: 24,
      fontSize: 13, lineHeight: 1.5,
    },
    sectionTitle: { fontSize: 14, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 16 },
    linkRow: { display: 'flex', gap: 12, marginTop: 24, flexWrap: 'wrap' },
    linkBtn: {
      padding: '10px 20px', borderRadius: 'var(--radius-sm)', fontSize: 14, fontWeight: 500,
      border: '1px solid var(--border)', background: 'transparent',
      color: 'var(--text)', cursor: 'pointer',
    },
    checkList: { listStyle: 'none', padding: 0, margin: '0 0 16px 0' },
    checkItem: { display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 13, color: 'var(--muted)', lineHeight: 1.6, marginBottom: 8 },
    checkIcon: { color: 'var(--green)', fontSize: 16, flexShrink: 0, marginTop: 1 },
  };

  return (
    <div style={s.wrap}>
      <div style={s.h1}>Get Started</div>
      <div style={s.sub}>
        Connect Claude to every API on the marketplace. One command.
      </div>

      {/* Requirements */}
      <div style={s.reqBox}>
        <strong style={{ color: 'var(--text)' }}>Before you start, make sure you have:</strong>
        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div>
            <a href="https://claude.ai/download" target="_blank" rel="noreferrer" style={{ color: 'var(--accent2)', fontWeight: 500 }}>Claude Desktop</a>
            <span style={{ color: 'var(--muted)' }}> — the app (not the website)</span>
          </div>
          <div>
            <a href="https://nodejs.org" target="_blank" rel="noreferrer" style={{ color: 'var(--accent2)', fontWeight: 500 }}>Node.js</a>
            <span style={{ color: 'var(--muted)' }}> — download the LTS version, install, <strong>restart your computer</strong></span>
          </div>
        </div>
      </div>

      {/* Step 1 — Get key */}
      <div style={s.step}>
        <div style={s.stepHeader}>
          <div style={s.stepNum}>1</div>
          <div style={s.stepTitle}>Get your API key</div>
        </div>
        <div style={s.stepDesc}>
          Create a free account and paste your key below:
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 12 }}>
          <button style={s.primaryBtn} onClick={() => navigate('getkey')}>
            Get API key
          </button>
          <span style={{ fontSize: 13, color: 'var(--muted)' }}>free, takes 30 seconds</span>
        </div>
        <input
          style={s.input}
          placeholder="Paste your API key here (am_live_...)"
          value={apiKey}
          onChange={e => setApiKey(e.target.value)}
        />
        {apiKey && !apiKey.startsWith('am_live_') && (
          <div style={{ color: '#ef4444', fontSize: 12, marginTop: 6 }}>Key should start with am_live_</div>
        )}
      </div>

      {/* Step 2 — Run setup */}
      <div style={s.step}>
        <div style={s.stepHeader}>
          <div style={s.stepNum}>2</div>
          <div style={s.stepTitle}>
            {hasKey ? 'Paste this into your terminal' : 'Paste your key above, then copy this command'}
          </div>
        </div>

        <div style={s.osTabs}>
          {[
            { key: 'windows', label: 'Windows' },
            { key: 'mac', label: 'Mac / Linux' },
          ].map(t => (
            <button key={t.key} style={s.osTab(os === t.key)} onClick={() => setOs(t.key)}>
              {t.label}
            </button>
          ))}
        </div>

        <div style={s.stepDesc}>
          Open <strong>{os === 'windows' ? 'PowerShell' : 'Terminal'}</strong> and paste:
        </div>

        <div style={s.codeWrap}>
          <div style={s.code}>{setupCmd}</div>
          <button
            style={{
              ...s.copyBtn,
              ...(hasKey ? {} : { opacity: 0.5 }),
            }}
            onClick={() => hasKey ? copy(setupCmd, 'setup') : null}
          >
            {copied === 'setup' ? 'Copied!' : hasKey ? 'Copy' : 'Enter key first'}
          </button>
        </div>

        <ul style={s.checkList}>
          {[
            'Installs the MCP connector automatically',
            'Configures Claude Desktop with your key',
            'Preserves your existing MCP servers',
          ].map(t => (
            <li key={t} style={s.checkItem}>
              <span style={s.checkIcon}>&#10003;</span>
              <span>{t}</span>
            </li>
          ))}
        </ul>

        <div style={{ fontSize: 14, color: 'var(--text)', fontWeight: 600, marginTop: 8 }}>
          After it says "Done!" → quit Claude Desktop → reopen it.
        </div>
      </div>

      {/* Done */}
      <div style={s.successBox}>
        <div style={{ fontWeight: 600, fontSize: 15, color: 'var(--green)', marginBottom: 6 }}>
          That's it. Ask Claude anything.
        </div>
        <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.6 }}>
          <em>"Use invokeAPI to list available builds"</em><br/>
          <em>"Call drc-pattern-scanner and show me BTCUSDT candle data"</em><br/>
          <em>"Check my invokeAPI balance"</em>
        </div>
      </div>

      <div style={s.divider} />

      {/* What your AI can do */}
      <div style={s.sectionTitle}>Available MCP tools</div>
      <div style={{ display: 'grid', gap: 10, marginBottom: 8 }}>
        {[
          { tool: 'list_builds', desc: 'Browse all APIs and pricing' },
          { tool: 'call_build', desc: 'Call any API on the marketplace' },
          { tool: 'get_balance', desc: 'Check your credit balance' },
          { tool: 'get_build', desc: 'Get details about a specific API' },
          { tool: 'submit_job', desc: 'Start long-running async tasks' },
        ].map(t => (
          <div key={t.tool} style={{ display: 'flex', gap: 12, alignItems: 'baseline', fontSize: 13 }}>
            <span style={{ ...s.highlight, flexShrink: 0 }}>{t.tool}</span>
            <span style={{ color: 'var(--muted)' }}>{t.desc}</span>
          </div>
        ))}
      </div>

      <div style={s.divider} />

      {/* Claude Code */}
      <div style={s.sectionTitle}>Using Claude Code?</div>
      <div style={s.codeWrap}>
        <div style={s.code}>{`claude mcp add invokeapi --transport streamable-http "https://invokeapi-production.up.railway.app/mcp" --header "Authorization: Bearer ${key}"`}</div>
        <button style={s.copyBtn} onClick={() => copy(`claude mcp add invokeapi --transport streamable-http "https://invokeapi-production.up.railway.app/mcp" --header "Authorization: Bearer ${key}"`, 'code')}>
          {copied === 'code' ? 'Copied!' : 'Copy'}
        </button>
      </div>

      <div style={s.divider} />

      {/* Links */}
      <div style={s.sectionTitle}>Links</div>
      <div style={s.linkRow}>
        <button style={s.linkBtn} onClick={() => navigate('marketplace')}>
          Browse marketplace
        </button>
        <button style={s.linkBtn} onClick={() => window.open('https://www.npmjs.com/package/invokeapi', '_blank')}>
          npm SDK
        </button>
        <button style={s.linkBtn} onClick={() => navigate('builder-docs')}>
          Builder docs
        </button>
      </div>
    </div>
  );
}
